import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '@/components/layout/SEOHead'
import { graph, organization, breadcrumbList } from '@/lib/jsonLd'

/**
 * Public live builder for the /embed widget. Lets bloggers configure language,
 * currency, attribution and iframe size, see the result render in real time,
 * and copy the ready-to-paste HTML.
 *
 * Companion to /widget-hipoteczny (the informational landing page).
 *
 * Layout: controls and live preview side-by-side so changing a param updates
 * the iframe without scrolling; ready-to-paste embed code and copy button at
 * the bottom of the page.
 */

const KREATOR_JSONLD = graph(
  {
    '@type': 'WebPage',
    '@id': 'https://ratygo.pl/widget-hipoteczny/kreator#webpage',
    name: 'Kreator widgetu kalkulatora kredytu hipotecznego',
    description:
      'Interaktywny kreator widgetu kalkulatora kredytu hipotecznego RatyGo. Dobierz język, walutę i wymiary, a następnie skopiuj gotowy kod do osadzenia na swojej stronie.',
    url: 'https://ratygo.pl/widget-hipoteczny/kreator',
    inLanguage: 'pl',
    isPartOf: { '@id': 'https://ratygo.pl#organization' },
  },
  organization(),
  breadcrumbList([
    { name: 'Strona główna', url: '/' },
    { name: 'Widget hipoteczny', url: '/widget-hipoteczny' },
    { name: 'Kreator', url: '/widget-hipoteczny/kreator' },
  ]),
)

const PROD_BASE = 'https://ratygo.pl'

const LANGS = [
  { value: 'pl', label: 'polski' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'français' },
  { value: 'es', label: 'español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'ru', label: 'русский' },
  { value: 'uk', label: 'українська' },
]

const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'CHF', 'UAH', 'RUB', 'CZK', 'HUF', 'RON', 'SEK', 'NOK', 'DKK', 'JPY', 'CAD', 'AUD']

const WIDTH_PRESETS = ['100%', '600', '500', '320']
const HEIGHT_PRESETS = ['420', '600', '750']

export default function EmbedTestPage() {
  const [lang, setLang] = useState('pl')
  const [currency, setCurrency] = useState('PLN')
  const [attr, setAttr] = useState('')
  const [width, setWidth] = useState('100%')
  const [height, setHeight] = useState('420')
  const [copied, setCopied] = useState(false)

  const previewSrc = useMemo(() => {
    const params = new URLSearchParams()
    if (lang !== 'pl') params.set('lang', lang)
    if (currency !== 'PLN') params.set('currency', currency)
    if (attr) params.set('attr', attr)
    const q = params.toString()
    return q ? `/embed?${q}` : '/embed'
  }, [lang, currency, attr])

  const productionSrc = `${PROD_BASE}${previewSrc}`

  const generatedCode = `<iframe src="${productionSrc}" width="${width}" height="${height}" style="border:0; border-radius:12px; max-width:100%;" loading="lazy" title="Kalkulator kredytu hipotecznego — RatyGo"></iframe>`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = generatedCode
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <SEOHead
        title="Kreator widgetu — Widget hipoteczny RatyGo"
        description="Interaktywny kreator widgetu kalkulatora kredytu hipotecznego. Wybierz język, walutę, wymiary i skopiuj gotowy kod do osadzenia na swojej stronie."
        slug="/widget-hipoteczny/kreator"
        keywords={[
          'kreator widgetu kalkulatora',
          'generator widgetu hipotecznego',
          'embed code kalkulator kredytu',
          'iframe kalkulator hipoteczny',
        ]}
        jsonLd={KREATOR_JSONLD}
      />

      <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
        <Link to="/" className="hover:text-foreground transition-colors">RatyGo</Link>
        <span>/</span>
        <Link to="/widget-hipoteczny" className="hover:text-foreground transition-colors">
          Widget hipoteczny
        </Link>
        <span>/</span>
        <span className="text-foreground">Kreator</span>
      </nav>

      <header className="mb-4">
        <h1 className="text-2xl font-bold text-foreground leading-tight">
          Kreator widgetu — dostosuj i skopiuj kod
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Wybierz język, walutę i wymiary, a następnie skopiuj gotowy kod{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">&lt;iframe&gt;</code> do osadzenia
          na swojej stronie.
        </p>
      </header>

      {/* Controls + Preview side-by-side */}
      <section className="grid gap-3 lg:grid-cols-[280px_1fr] rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-violet-50/60 p-3">
        {/* LEFT: controls */}
        <div className="space-y-3">
          <label className="text-sm block">
            <span className="block font-semibold text-foreground text-xs mb-1">Język interfejsu</span>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
            >
              {LANGS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.value} — {l.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm block">
            <span className="block font-semibold text-foreground text-xs mb-1">Waluta</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="text-sm block">
            <span className="block font-semibold text-foreground text-xs mb-1">
              Atrybucja <span className="text-muted-foreground font-normal">(opcjonalnie)</span>
            </span>
            <input
              type="text"
              value={attr}
              onChange={(e) => setAttr(e.target.value)}
              placeholder="np. Powered by"
              maxLength={60}
              className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
            />
          </label>

          <div>
            <label className="text-sm block">
              <span className="block font-semibold text-foreground text-xs mb-1">Szerokość</span>
              <input
                type="text"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder="100% lub 320"
                className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm font-mono"
              />
            </label>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {WIDTH_PRESETS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWidth(w)}
                  className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                    width === w
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-white hover:bg-muted'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm block">
              <span className="block font-semibold text-foreground text-xs mb-1">Wysokość (px)</span>
              <input
                type="text"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="500"
                className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm font-mono"
              />
            </label>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {HEIGHT_PRESETS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setHeight(h)}
                  className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                    height === h
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-white hover:bg-muted'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="min-w-0">
          <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-2 h-full flex items-start justify-center overflow-auto">
            <iframe
              key={previewSrc}
              src={previewSrc}
              width={width}
              height={height}
              style={{ border: 0, borderRadius: 12, maxWidth: '100%', display: 'block' }}
              loading="lazy"
              title="Podgląd kalkulatora kredytu hipotecznego — RatyGo"
              className="bg-white shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* BOTTOM: ready-to-paste code + copy button */}
      <section className="mt-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h2 className="text-sm font-semibold text-foreground">Gotowy kod do osadzenia</h2>
          <button
            type="button"
            onClick={handleCopy}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
              copied
                ? 'bg-emerald-500 text-white'
                : 'bg-foreground text-background hover:bg-foreground/90'
            }`}
            aria-label={copied ? 'Skopiowano' : 'Kopiuj kod'}
          >
            {copied ? '✓ Skopiowano' : 'Kopiuj kod'}
          </button>
        </div>
        <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto text-xs font-mono leading-relaxed border border-slate-800 whitespace-pre-wrap break-all">
          <code>{generatedCode}</code>
        </pre>
      </section>
    </div>
  )
}
